import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LeadGenerationConfigurationService } from '../shared/services/lead-generation-configuration.service';
import { ActivatedRoute, Router } from '@angular/router';
import DeviceType from '../shared/domain/device.type';
import { AmplitudeService } from '../../shared/services/amplitude.service';
import { FormGroupWrapper } from '../shared/domain/FormGroupWrapper';
import { ValidateUniqueName } from '../projects/validators/projectUniqueName.validator';
import { ProjectsService } from '../projects/projects.service';
import { initialConfig } from '../shared/domain/initialConfig';
import { IState } from '../../shared/models/state';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Configuration } from '../shared/domain/configuration.model';
import { getActiveProjectSelector, getCreatedProjectSnapshotSelector } from '../projects/projects.reducer';
import { CreateProjectAction, UpdateProjectConfigurationAction } from '../projects/projects.action';
import { CrazyCallConfigService } from '../shared/services/crazycall-config.service';
import { LanguageService } from '../../shared/services/language.service';
import { TranslateService } from '@ngx-translate/core';
import rawDomainRegex from '../shared/regex/rawDomainRegex';
import { markFormAsTouched } from '../shared/reactive-forms/forEachElementInForm';

@Component({
  selector: 'cc-lg-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SettingsComponent implements OnInit {
  public config: Configuration;
  public creatingNewProject: boolean;
  public settingsForm: FormGroup;
  public account: string;
  public deviceTypes: string[];
  public isCodeInstalled: boolean;
  public displayConditionsFormWrapper: FormGroupWrapper = { form: null };
  public workingHoursFormWrapper: FormGroupWrapper = { form: null };
  private isSubmitted = false;
  private currentLanguage: string;

  constructor(
    private leadGenerationConfiguration: LeadGenerationConfigurationService,
    private route: ActivatedRoute,
    private router: Router,
    private projectsService: ProjectsService,
    private amplitudeService: AmplitudeService,
    private fb: FormBuilder,
    private store: Store<IState>,
    private toastr: ToastrService,
    private crazyCallConfigService: CrazyCallConfigService,
    private languageService: LanguageService,
    public translate: TranslateService
  ) {
    this.account = route.snapshot.data['account'];
    this.deviceTypes = Object.values(DeviceType);
    this.isCodeInstalled = false;
    this.currentLanguage = this.languageService.getCookieLanguage();

    this.store.select(getActiveProjectSelector).subscribe((config: Configuration | null) => {
      if (config && config.projectId) {
        this.config = config;
      } else {
        this.config = initialConfig(this.account, this.currentLanguage || 'en');
        this.creatingNewProject = true;
      }
    });

    this.store.select(getCreatedProjectSnapshotSelector).subscribe((config: Configuration | null) => {
      if (config) {
        this.config = config;
      }
    });
  }

  ngOnInit() {
    this.logAmplitude('Init');
    this.initForms();
    this.fillForms();
    window.scroll(0, 0);
  }

  private initForms() {
    const urlValidators = Validators.pattern(/.*?\..{2,}/);
    this.settingsForm = this.fb.group({
      name: [
        'WebCall Project',
        [Validators.required],
        ValidateUniqueName(this.projectsService, this.leadGenerationConfig.name)
      ],
      domainName: ['', Validators.pattern(rawDomainRegex)],
      allowSubdomains: [''],
      ignoreDisplayLimit: [''],
      displayOnDeviceType: [''],
      active: [''],
      timeBetweenShownPopups: [''],
      timeBetweenSubmittedPopups: [''],
      webhookUrl: ['', urlValidators]
    });
  }

  private fillForms() {
    Object.entries(this.leadGenerationConfig).forEach(([key, value]) => {
      if (this.settingsForm.controls.hasOwnProperty(key)) {
        this.settingsForm.controls[key].setValue(value);
      }
    });
  }

  get leadGenerationConfig() {
    return this.config;
  }

  get installedCodeUrl() {
    return this.config.domainName;
  }

  get popupLink() {
    return (
      this.settingsForm.get('domainName').value +
      '?crazycall-open-dialog=true&crazycall-project=' +
      this.leadGenerationConfig.projectId
    );
  }

  onSubmit() {
    if (!this.settingsForm.valid) {
      markFormAsTouched(this.settingsForm);
      return;
    }
    this.logAmplitude('Submit', this.mapFormValuesToConfig());
    this.isSubmitted = true;
    this.creatingNewProject ? this.createProject() : this.updateConfig();
  }

  createProject() {
    this.config = this.mapFormValuesToConfig();
    this.store.dispatch(new CreateProjectAction(this.config));
  }

  private mapFormValuesToConfig(config: Configuration = this.config): Configuration {
    return {
      ...config,
      ...this.settingsForm.getRawValue(),
      displayConditions: this.displayConditionsFormWrapper.form.get('displayConditions').value,
      workingHours: this.workingHoursFormWrapper.form.getRawValue()
    };
  }

  updateConfig() {
    this.config = this.mapFormValuesToConfig();
    this.store.dispatch(new UpdateProjectConfigurationAction(this.config));
  }

  isFormValid() {
    return (
      // this.settingsForm.valid &&
      (!this.displayConditionsFormWrapper.form || this.displayConditionsFormWrapper.form.valid) &&
      (!this.workingHoursFormWrapper.form ||
        this.workingHoursFormWrapper.form.valid ||
        !this.workingHoursFormWrapper.form.get('enabled').value)
    );
  }

  private logAmplitude(message: string, event?: any) {
    this.amplitudeService.logEvent(`Panel:WebCall:Settings: ${message}`, event);
  }
}
